import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Locales } from '@carsys/enums/locales'
import useQuery from '@cbgms/base/http/use-query'
import { CountryDropdownItem } from '@carsys/form/src/CountriesDropdownBase'
import { getCountries } from './actions'
import { ICountry, SupportedCountryCode } from '@cbgms/api'
import { allowedFlags } from './allowed-flags'

interface Props {
  children: (countryOptions: CountryDropdownItem[]) => JSX.Element
}

const isoCodeToFlag = (isoCode: string) => {
  const code = isoCode.toLowerCase()
  if (allowedFlags.indexOf(code) > -1) {
    return code
  }

  return null
}

export const mapSupportedCountryToDropdownOptions = (countries: readonly SupportedCountryCode[]) => {
  return countries.map(country => ({
    key: country.Code,
    value: country.Code,
    flag: isoCodeToFlag(country.Code),
    text: country.FullName
  }))
}

export const mapAllCountries = (countries: ICountry[]): SupportedCountryCode[] =>
  countries.map(country => ({
    Code: country.IsoCode,
    FullName: country.Name,
    RegistrationCode: country.IsoCode,
    Imperial: country.Imperial
  }))

export const CountriesOptionsWrapper: React.FC<Props> = ({ children }) => {
  const { i18n } = useTranslation()
  const dispatch = useDispatch()

  const loadCountries = useQuery(['GET_COUNTRIES', i18n.language], async () => {
    const { Data } = await dispatch(getCountries(i18n.language as Locales))
    return mapAllCountries(Data)
  })

  const countryOptions = React.useMemo(() => {
    if (!loadCountries.data || loadCountries.data.length === 0) return []
    return mapSupportedCountryToDropdownOptions(loadCountries.data)
  }, [loadCountries.data])

  return children(countryOptions)
}
